import { useEffect } from 'react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { Navigate, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button, Colors, Display } from '../Components/Button/Button';
import { store } from 'Models/Store';
import alert from '../../Util/ToastifyUtils';
import { PasswordInput } from 'Views/Components/New/PasswordInput';
import { resetPassword, resetPasswordRequest } from 'Services/Api/AccountService';
import { UPDATE_PASSWORD_PAGE_TITLE } from 'ConstantPageNames';
import { PasswordConfiguration, PasswordOptions } from 'Global';
import { camelCase } from 'Util/StringUtils';
import { getPasswordOptionsForRole, validatePassword } from 'Validators/Functions/HumanWritten/Password';
import { stringNotEmpty } from 'Util/TypeGuards';
import { observable } from 'mobx';
import { PENDING_PROFILE_CHECK_FLAG } from 'Modules/Auth/PostLoginProfileCheck';
import { themeStore } from 'Models/ThemeStore';
import Card from 'Views/Components/_HumanWritten/Card/Card';
import { Flex } from 'Views/Components/Flex/Flex';
import useDeviceDetect from 'Hooks/useDeviceDetect';

interface IResetPasswordState {
	token: string;
	username: string;
	password: string;
	confirmPassword: string;
	role: string;
	errors: {
		password?: string;
		confirmPassword?: string;
		[attr: string]: string | undefined;
	};
}

/**
 * State of the "your link is no longer usable, here's a new one" recovery path.
 *
 * The reset endpoint distinguishes the two ways this page can fail, which is what makes the recovery
 * offer targeted rather than noise: a password that breaks the configured policy comes back 400 with
 * the specific rule descriptions, while a token that has expired, has already been used, or belongs
 * to no one comes back 401. Only the second is worth offering a new link for — showing it after
 * "password needs an uppercase letter" would send people round a pointless loop.
 */
interface ILinkRecoveryState {
	/** The reset endpoint rejected the token itself, so the form can no longer succeed. */
	invalid: boolean;
	/** A replacement is being requested. */
	sending: boolean;
	/** A replacement has been requested at least once. */
	sent: boolean;
}

function ResetPasswordPage() {
	const { isMobile } = useDeviceDetect();

	useEffect(() => {
		document.title = UPDATE_PASSWORD_PAGE_TITLE;
	}, []);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [resetPasswordState, setResetPasswordState] = React.useState<IResetPasswordState>(() =>
		observable({
			token: searchParams.get('token') || '',
			username: searchParams.get('username') || '',
			password: '',
			confirmPassword: '',
			role: camelCase(searchParams.get('role') || ''),
			errors: {},
		}),
	);

	const [linkRecovery, setLinkRecovery] = React.useState<ILinkRecoveryState>({
		invalid: false,
		sending: false,
		sent: false,
	});

	if (store.loggedIn) {
		return <Navigate to="/" />;
	}

	const activateAccount = searchParams.get('activateAccount');
	const migratedUser = searchParams.get('migratedUser');

	// Migrated customers arrive from a bulk invite and read "activation"; everyone else asked for a
	// password reset. Same link either way, so only the wording changes.
	const isActivation = !!activateAccount || !!migratedUser;
	const linkNoun = isActivation ? 'activation link' : 'password reset link';

	let title = 'Reset password';
	if (migratedUser) {
		title = 'Activate your account';
	} else if (activateAccount) {
		title = 'Activate account';
	}

	const passwordRestrictions: string[] = [];
	const options: PasswordOptions | PasswordConfiguration = getPasswordOptionsForRole(
		store.passwordConfiguration,
		resetPasswordState.role,
	);

	if (options.requiredLength && options.requiredLength > 0) {
		passwordRestrictions.push(`${options.requiredLength} characters`);
	}
	if (options.requiredUniqueChars && options.requiredUniqueChars > 1) {
		passwordRestrictions.push(`${options.requiredUniqueChars} unique characters`);
	}
	if (options.requireNonAlphanumeric) {
		passwordRestrictions.push('At least one non alphanumeric character');
	}
	if (options.requireLowercase) {
		passwordRestrictions.push('At least one lowercase character');
	}
	if (options.requireUppercase) {
		passwordRestrictions.push('At least one uppercase character');
	}
	if (options.requireDigit) {
		passwordRestrictions.push('At least one digit');
	}
	if (passwordRestrictions.length === 0) {
		passwordRestrictions.push('12 characters');
	}

	const onResetPasswordClicked = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const newErrors: IResetPasswordState['errors'] = {};

		const passwordError = validatePassword(
			getPasswordOptionsForRole(store.passwordConfiguration, resetPasswordState.role),
			resetPasswordState.password,
		);
		if (!resetPasswordState.password) {
			newErrors.password = 'This field is required';
		} else if (stringNotEmpty(passwordError)) {
			newErrors.password = passwordError;
		} else if (!resetPasswordState.confirmPassword) {
			newErrors.confirmPassword = 'This field is required';
		} else if (resetPasswordState.confirmPassword !== resetPasswordState.password) {
			newErrors.confirmPassword = 'Password and confirm password does not match';
		}

		setResetPasswordState({ ...resetPasswordState, errors: newErrors });

		if (Object.keys(newErrors).length === 0) {
			resetPassword(
				resetPasswordState.token,
				resetPasswordState.username,
				resetPasswordState.password,
				!!activateAccount || !!migratedUser,
			)
				.then(() => {
					alert('Your password has been reset', 'success');
					// Reset password now logs people in on success
					// Do a browser redirect so it will reload the user auth js
					// If we did a react router redirect then the user js would not reload
					sessionStorage.setItem(PENDING_PROFILE_CHECK_FLAG, '1');
					window.location.href = '/';
				})
				.catch(errors => {
					// 401 means the endpoint rejected the token, not the password — the form can never
					// succeed from here, so a toast that fades after a few seconds and says "contact
					// staff" is the wrong response. Swap the form for an inline offer of a new link,
					// which the server can already issue unaided.
					if (errors?.response?.status === 401) {
						setLinkRecovery({ invalid: true, sending: false, sent: false });
						return;
					}

					const errorMsgs = errors?.response?.data?.errors?.map((error: any) => (
						<p key={error.message}>{error.message}</p>
					));
					alert(
						<div>
							<h6>Password could not be changed</h6>
							{errorMsgs}
							<p style={{ margin: 0 }}>If you continue to have trouble, please contact staff for help.</p>
						</div>,
						'error',
					);
				});
		}
	};

	const onSendNewLinkClicked = () => {
		setLinkRecovery(prev => ({ ...prev, sending: true }));

		// The same endpoint the forgot-password page uses. It routes anyone still carrying
		// NeedsPasswordReset back through a set-a-password email — the migrated-user one for accounts
		// the CSV migration created, the activate-account one for accounts staff created — so a
		// customer who has never set a password gets the activation experience again rather than a
		// bare password reset.
		resetPasswordRequest(resetPasswordState.username)
			.then(() => {
				setLinkRecovery({ invalid: true, sending: false, sent: true });
			})
			.catch(() => {
				setLinkRecovery(prev => ({ ...prev, sending: false }));
				alert(
					<div>
						<h6>Could not send a new link</h6>
						<p style={{ margin: 0 }}>
							Please check your connection and try again, or contact staff for help.
						</p>
					</div>,
					'error',
				);
			});
	};

	return (
		<div className="login-page">
			<Link className="logo-link" to="/home">
				<img
					src={isMobile ? themeStore.logoSecondaryLightURL : themeStore.logoPrimaryLightURL}
					alt="Company Logo"
				/>
			</Link>
			<div
				className="top-section"
				style={{
					backgroundImage: `url(${themeStore.loginBackgroundImageURL})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			/>
			<div className="bottom-section" />
			<div className="center-section">
				<div className="login-flow">
					{linkRecovery.invalid ? (
						<Card>
							<h5>
								<FormattedMessage
									id="reset-password__link-invalid-header"
									defaultMessage="This link is no longer valid"
								/>
							</h5>
							<p className="body--content" style={{ margin: 0 }}>
								<FormattedMessage
									id="reset-password__link-invalid-body"
									defaultMessage="For your security, {linkNoun}s expire 24 hours after being sent and can only be used once."
									values={{ linkNoun }}
								/>
							</p>
							{stringNotEmpty(resetPasswordState.username) ? (
								<Flex direction="col" gap="xs">
									{linkRecovery.sent && (
										<p style={{ margin: 0 }}>
											<span style={{ fontWeight: 600 }}>Email sent!</span> Check your inbox for a
											new link. It may take a minute to arrive.
										</p>
									)}
									{!linkRecovery.sent && (
										<p style={{ margin: 0 }}>
											We can send a new one to <strong>{resetPasswordState.username}</strong>.
										</p>
									)}
									<Button
										type="button"
										display={Display.Solid}
										colors={Colors.Primary}
										newStyles
										disabled={linkRecovery.sending}
										onClick={onSendNewLinkClicked}
									>
										{/* eslint-disable-next-line no-nested-ternary */}
										{linkRecovery.sending
											? 'Sending...'
											: linkRecovery.sent
												? 'Send another link'
												: 'Send me a new link'}
									</Button>
									<Button
										type="button"
										display={Display.Outline}
										colors={Colors.Secondary}
										newStyles
										onClick={() => navigate('/login')}
									>
										Back to login
									</Button>
								</Flex>
							) : (
								// No username in the URL means there is nobody to send to — hand them to the
								// forgot-password page, which asks for the address.
								<Flex direction="col" gap="xs">
									<p style={{ margin: 0 }}>
										Enter your email address on the next page and we'll send you a new one.
									</p>
									<Button
										type="button"
										display={Display.Solid}
										colors={Colors.Primary}
										newStyles
										onClick={() => navigate('/reset-password-request')}
									>
										Get a new link
									</Button>
								</Flex>
							)}
						</Card>
					) : (
						<form onSubmit={onResetPasswordClicked}>
							<Card>
								<h5>{title}</h5>
								{migratedUser && (
									<p style={{ margin: 0 }} className="body--content">
										<FormattedMessage
											id="migrated-user-activation__body"
											defaultMessage="Welcome back! We've upgraded our system and your details and bookings have come across with you. To keep your account secure, please set a new password below to finish activating your account."
										/>
									</p>
								)}
								<div>
									<p style={{ margin: 0 }}>Password must contain:</p>
									<ul style={{ margin: 0, paddingLeft: '1.25rem', listStyle: 'disc' }}>
										{passwordRestrictions.map(x => (
											<li key={x} style={{ fontSize: '0.875rem' }}>
												{x}
											</li>
										))}
									</ul>
								</div>
								<PasswordInput
									id="new_password"
									className="new-password"
									label="New password"
									placeholder="New password"
									required
									value={resetPasswordState.password}
									error={resetPasswordState.errors.password}
									onChange={e => {
										const { value } = e.target;
										setResetPasswordState(prev => ({
											...prev,
											password: value,
											errors: { ...prev.errors, password: undefined },
										}));
									}}
								/>
								<PasswordInput
									id="confirm_password"
									className="confirm-password"
									label="Confirm password"
									placeholder="Confirm password"
									required
									value={resetPasswordState.confirmPassword}
									error={resetPasswordState.errors.confirmPassword}
									onChange={e => {
										const { value } = e.target;
										setResetPasswordState(prev => ({
											...prev,
											confirmPassword: value,
											errors: { ...prev.errors, confirmPassword: undefined },
										}));
									}}
								/>
								<Flex direction="col" gap="xs">
									<Button type="submit" display={Display.Solid} colors={Colors.Primary} newStyles>
										Confirm
									</Button>
								</Flex>
							</Card>
						</form>
					)}
				</div>
			</div>
		</div>
	);
}

export default observer(ResetPasswordPage);
